import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import arrowIcon from '../../assets/gray-arrow-icon.png'
import { type PaginationProps } from './props'
import * as S from './style'

export function Pagination ({ currentPage, onPageChange, totalPages }: PaginationProps): ReactElement {
  const [current, setCurrent] = useState(currentPage)
  const [pageGroupStart, setPageGroupStart] = useState(1)
  const offset = window.innerWidth > 519 ? 5 : 3

  const pageNumbers = useMemo(() => {
    if (totalPages <= offset) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    return Array.from({ length: Math.min(offset, totalPages - pageGroupStart + 1) }, (_, i) => pageGroupStart + i)
  }, [offset, pageGroupStart, totalPages])

    const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrent(page)
      onPageChange(page)

      const newGroupStart = Math.floor((page - 1) / offset) * offset + 1
      setPageGroupStart(newGroupStart)
    }
  }, [offset, onPageChange, totalPages])

  const handleNextGroup = useCallback(() => {
    const nextGroupStart = pageGroupStart + offset
    if (nextGroupStart <= totalPages) {
      setPageGroupStart(nextGroupStart)
      setCurrent(nextGroupStart)
      onPageChange(nextGroupStart)
    }
  }, [offset, onPageChange, pageGroupStart, totalPages])

  const handlePrevGroup = useCallback(() => {
    const prevGroupStart = pageGroupStart - offset
    if (prevGroupStart >= 1) {
      setPageGroupStart(prevGroupStart)
      setCurrent(prevGroupStart)
      onPageChange(prevGroupStart)
    }
  }, [offset, onPageChange, pageGroupStart])

  useEffect(() => {
    setCurrent(currentPage)
    const newGroupStart = Math.floor((currentPage - 1) / offset) * offset + 1
    setPageGroupStart(newGroupStart)
  }, [currentPage, offset])

  if (totalPages <= 1) return <></>

  return <S.Container>
    <S.ArrowButton
      onClick={() => handlePageChange(current - 1)}
      disabled={current === 1}
    >
      <img src={arrowIcon} alt="" />
    </S.ArrowButton>
    {(pageGroupStart > 1 && totalPages > offset) && <S.PageButton disabled={current === 1} onClick={() => handlePageChange(1)}>1</S.PageButton>}
    {(current > 1 && pageGroupStart - (offset - 1) > 1) && <S.PageButton onClick={handlePrevGroup}>...</S.PageButton>}
    {pageNumbers.map((page) => <S.PageButton
      key={page}
      className={page === current ? 'active' : ''}
      onClick={() => handlePageChange(page)}
    >
      {page}
    </S.PageButton>)}
    {(pageGroupStart + offset <= totalPages) && <S.PageButton onClick={handleNextGroup}>...</S.PageButton>}
    {(totalPages > offset && pageGroupStart + (offset - 1) < totalPages) && <S.PageButton
      className={totalPages === current ? 'active' : ''}
      onClick={() => handlePageChange(totalPages)}
    >
      {totalPages}
    </S.PageButton>}
    <S.ArrowButton
      onClick={() => handlePageChange(current + 1)}
      disabled={current === totalPages}
    >
      <img src={arrowIcon} alt="" />
    </S.ArrowButton>
  </S.Container>
}
